// Quarter-turn rotation for the markup editor.
//
// Rotation is an orientation correction on the PHOTO — applied first, before
// crop and before drawings render. Drawings are stored in "rotation-corrected"
// space (see [[image-markup-editor.spec]] § Coordinate systems), which means
// canvas space and drawing space stay identical at every rotation. That is the
// whole point of this design: pointer input, the text DOM overlay layer, text
// drag and pinch-resize all keep working untouched.
//
// The cost is that rotating has to re-map the stored coordinates. That is what
// this module does.

import type { CropRect } from "./crop-overlay";
import type { Drawing, Point, TextDrawing } from "./types";

/** Clockwise degrees. */
export type Rotation = 0 | 90 | 180 | 270;

export const ROTATIONS: Rotation[] = [0, 90, 180, 270];

/** Normalize an arbitrary turn count to 0–3. */
export function normalizeTurns(turns: number): number {
    return ((Math.round(turns) % 4) + 4) % 4;
}

export function rotationToTurns(rotation: Rotation): number {
    return rotation / 90;
}

export function turnsToRotation(turns: number): Rotation {
    return (normalizeTurns(turns) * 90) as Rotation;
}

export function addTurns(rotation: Rotation, turns: number): Rotation {
    return turnsToRotation(rotationToTurns(rotation) + turns);
}

/** Quarter-turns needed to get from `from` back to `to`. */
export function turnsBetween(from: Rotation, to: Rotation): number {
    return normalizeTurns(rotationToTurns(to) - rotationToTurns(from));
}

/**
 * Coerce a persisted `annotations.rotation` into a `Rotation`.
 *
 * Anything unrecognized — absent, null, 0, or junk from an older/newer writer —
 * comes back `undefined`, which the editor treats as no rotation. That is
 * exactly the pre-rotation behavior, so old records keep working untouched.
 */
export function toRotation(value: unknown): Rotation | undefined {
    return value === 90 || value === 180 || value === 270 ? value : undefined;
}

/** Odd quarter-turns swap width and height. */
export function getRotatedSize(
    width: number,
    height: number,
    rotation: Rotation,
): { width: number; height: number } {
    return rotation === 90 || rotation === 270
        ? { width: height, height: width }
        : { width, height };
}

/**
 * Map a point through ONE clockwise quarter-turn of a `? × spaceH` space. The
 * result lives in the swapped space, whose height is the old width.
 *
 * Only the height is needed: a clockwise turn sends `(x, y)` to `(H - y, x)`.
 * Subtraction only — no multiplication, no trig — so four turns land back on
 * whole-pixel inputs exactly, and on fractional ones within an ulp. Nothing
 * compounds the way a trig/matrix implementation would over repeated turns.
 */
export function turnPoint(p: Point, spaceH: number): Point {
    return { x: spaceH - p.y, y: p.x };
}

/**
 * Apply the canvas transform that maps rotation-corrected space back onto the
 * raw source image, so `drawImage(img, 0, 0, imgW, imgH)` paints the photo in
 * its corrected orientation. `imgW`/`imgH` are the RAW (unrotated) dimensions.
 */
export function applyRotationTransform(
    ctx: CanvasRenderingContext2D,
    rotation: Rotation,
    imgW: number,
    imgH: number,
): void {
    switch (rotation) {
        case 90:
            ctx.translate(imgH, 0);
            ctx.rotate(Math.PI / 2);
            break;
        case 180:
            ctx.translate(imgW, imgH);
            ctx.rotate(Math.PI);
            break;
        case 270:
            ctx.translate(0, imgW);
            ctx.rotate(-Math.PI / 2);
            break;
        case 0:
            break;
    }
}

/** Map an axis-aligned rect through one clockwise quarter-turn. */
export function turnRect(rect: CropRect, spaceH: number): CropRect {
    // The old bottom-left corner becomes the new top-left.
    const topLeft = turnPoint({ x: rect.x, y: rect.y + rect.height }, spaceH);
    return {
        x: topLeft.x,
        y: topLeft.y,
        width: rect.height,
        height: rect.width,
    };
}

/**
 * Measures a text drawing's rendered box. Mirrors the wrap + padding maths in
 * `canvas-utils.drawText` — supplied by the caller so this module stays free of
 * canvas plumbing and stays trivially testable.
 */
export type TextSizeMeasurer = (
    drawing: TextDrawing,
) => { width: number; height: number } | null;

/**
 * Map one drawing through a single clockwise quarter-turn.
 *
 * Pen and shape geometry rotates with the photo — a circle drawn around a
 * defect must stay around that defect. Text does NOT rotate as a glyph: it has
 * to stay upright and readable, so only its box CENTRE is mapped and the
 * top-left is re-derived from the (unchanged) box size. Mapping the stored
 * top-left directly would shunt the text by its own width/height every turn.
 */
export function turnDrawing(
    drawing: Drawing,
    spaceH: number,
    measureText: TextSizeMeasurer,
): Drawing {
    switch (drawing.type) {
        case "pen":
            return {
                ...drawing,
                points: drawing.points.map((p) => turnPoint(p, spaceH)),
            };
        case "shape":
            return {
                ...drawing,
                start: turnPoint(drawing.start, spaceH),
                end: turnPoint(drawing.end, spaceH),
            };
        case "text": {
            const size = measureText(drawing);
            if (!size) {
                // No measuring context available — degrade to the plain point
                // map rather than dropping the annotation. It may sit offset by
                // its own box size, but it stays on the photo.
                return { ...drawing, position: turnPoint(drawing.position, spaceH) };
            }
            const centre = turnPoint(
                {
                    x: drawing.position.x + size.width / 2,
                    y: drawing.position.y + size.height / 2,
                },
                spaceH,
            );
            return {
                ...drawing,
                position: {
                    x: centre.x - size.width / 2,
                    y: centre.y - size.height / 2,
                },
            };
        }
    }
}

/**
 * Apply `turns` clockwise quarter-turns to a drawing list, tracking the space
 * dimensions as they swap on each turn. `spaceW`/`spaceH` describe the space
 * the drawings are currently in (i.e. the current canvas size).
 */
export function turnDrawings(
    drawings: Drawing[],
    turns: number,
    spaceW: number,
    spaceH: number,
    measureText: TextSizeMeasurer,
): Drawing[] {
    const n = normalizeTurns(turns);
    let result = drawings;
    let w = spaceW;
    let h = spaceH;
    for (let i = 0; i < n; i++) {
        result = result.map((d) => turnDrawing(d, h, measureText));
        [w, h] = [h, w];
    }
    return result;
}

/** `turnDrawings`, for a crop rect. */
export function turnRectBy(
    rect: CropRect,
    turns: number,
    spaceW: number,
    spaceH: number,
): CropRect {
    const n = normalizeTurns(turns);
    let result = rect;
    let w = spaceW;
    let h = spaceH;
    for (let i = 0; i < n; i++) {
        result = turnRect(result, h);
        [w, h] = [h, w];
    }
    return result;
}
